import React from 'react';
import { Link } from 'react-router-dom';
import {
  Building2,
  Calculator,
  FileText,
  CheckCircle,
  ArrowRight,
  Shield,
  TrendingUp,
  Users,
  ClipboardList,
  Globe,
  AlertTriangle,
  Briefcase
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import usePageSeo from '../hooks/usePageSeo';
import JsonLd from '../components/JsonLd';
import { breadcrumbSchema, serviceSchema } from '../utils/seoSchemas';

import RelatedServices from '../components/RelatedServices';
const CorporateTaxUAE = () => {
  usePageSeo({
    title: 'Corporate Tax Services in UAE | 9% CT Compliance',
    description:
      'Expert UAE Corporate Tax services — FTA registration, return filing, transfer pricing & advisory. Avoid penalties with WPAA, 18+ years of expertise.',
    path: '/corporate-tax-uae',
    image: '/og/corporate-tax-uae.png'
  });

  const services = [
    {
      title: 'Corporate Tax Registration (FTA)',
      description: 'End-to-end assistance registering your business with the Federal Tax Authority for UAE Corporate Tax.',
      icon: FileText
    },
    {
      title: 'Corporate Tax Return Filing',
      description: 'Accurate, on-time preparation and filing of your annual Corporate Tax return with the FTA.',
      icon: Calculator
    },
    {
      title: 'Tax Assessment & Computation',
      description: 'Detailed computation of taxable income and tax liability with proper adjustments and reliefs.',
      icon: ClipboardList
    },
    {
      title: 'Free Zone Tax Compliance',
      description: 'Expert guidance for Free Zone entities to qualify and maintain 0% rate on qualifying income.',
      icon: Globe
    },
    {
      title: 'Transfer Pricing',
      description: 'Local File, Master File, and benchmarking documentation for related party transactions.',
      icon: Building2
    },
    {
      title: 'Ongoing Tax Advisory & Planning',
      description: 'Strategic tax planning and continuous advisory to optimise your corporate tax position.',
      icon: TrendingUp
    }
  ];

  const whoNeedsCT = [
    { text: 'Businesses with income above AED 375,000', icon: TrendingUp },
    { text: 'Free Zone companies with mainland or related party transactions', icon: Globe },
    { text: 'SMEs and startups preparing for first CT filing', icon: Briefcase },
    { text: 'Companies requiring tax structuring and compliance support', icon: Shield }
  ];

  const whyChooseUs = [
    'UAE Corporate Tax Experts',
    'Practical, business-focused advice',
    'Experience with Free Zone & Mainland entities',
    'Transparent and cost-effective pricing'
  ];

  return (
    <div className="min-h-screen">
      <JsonLd
        id="ld-ct-service"
        data={serviceSchema({
          name: 'Corporate Tax Services in UAE',
          description:
            'End-to-end UAE Corporate Tax services: FTA registration, return filing, tax computation, Free Zone compliance, transfer pricing and advisory.',
          path: '/corporate-tax-uae',
          serviceType: 'Corporate Tax Services'
        })}
      />
      <JsonLd
        id="ld-ct-breadcrumb"
        data={breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Services', path: '/services' },
          { name: 'Corporate Tax UAE', path: '/corporate-tax-uae' }
        ])}
      />
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 bg-gradient-to-br from-teal-900 via-teal-800 to-gray-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-teal-400 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-teal-300 rounded-full filter blur-3xl"></div>
        </div>
        <div className="container mx-auto relative z-10">
          <div className="max-w-4xl">
            <div className="flex items-center space-x-2 mb-6">
              <Building2 className="text-teal-400" size={32} />
              <span className="text-teal-400 font-semibold text-sm uppercase tracking-wider">Corporate Tax Services</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Corporate Tax Services in UAE
            </h1>
            <p className="text-2xl text-teal-200 mb-6 font-semibold">
              9% CT Compliance Made Simple
            </p>
            <p className="text-xl text-gray-300 mb-6 leading-relaxed max-w-3xl">
              The UAE Corporate Tax regime (9%) is now fully applicable to businesses. Ensuring compliance is essential to avoid penalties and optimize your tax position.
            </p>
            <p className="text-lg text-gray-400 mb-8 max-w-3xl">
              At White Paper Accounts Auditing (WPAA), we provide end-to-end Corporate Tax services — from registration to filing and advisory.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact?service=corporate-tax">
                <Button size="lg" className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-6 text-lg" data-testid="ct-consultation-btn">
                  Get Tax Consultation
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <a href="tel:+971501219341" data-testid="ct-call-btn">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                  Call Us Now
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Our Corporate Tax Services */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider">Our Expertise</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-6">Our Corporate Tax Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive Corporate Tax solutions tailored for businesses operating in the UAE.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className="border-2 hover:border-teal-500 transition-all hover:shadow-lg">
                  <CardContent className="p-6">
                    <div className="bg-teal-100 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="text-teal-600" size={28} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                    <p className="text-gray-600">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who Needs Corporate Tax Services */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider">Target Clients</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-6">Who Needs Corporate Tax Services?</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {whoNeedsCT.map((item, index) => {
              const Icon = item.icon;
              return (
                <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all">
                  <CardContent className="p-6 flex items-start space-x-4">
                    <div className="bg-teal-100 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="text-teal-600" size={24} />
                    </div>
                    <p className="text-gray-700 font-medium pt-2">{item.text}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose WPAA */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider">Why WPAA</span>
              <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-8">Why Choose WPAA?</h2>
              <div className="space-y-4">
                {whyChooseUs.map((item, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="text-teal-600 flex-shrink-0" size={24} />
                    <p className="text-lg text-gray-700">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-8 text-white">
              <Users className="mb-4" size={48} />
              <h3 className="text-2xl font-bold mb-4">Experienced Corporate Tax Team</h3>
              <p className="text-teal-100 mb-6">
                Our team of qualified tax professionals brings deep expertise across Free Zone, Mainland, and cross-border entities — ensuring you remain compliant and tax-efficient.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-3xl font-bold">18+</p>
                  <p className="text-sm text-teal-200">Years Experience</p>
                </div>
                <div>
                  <p className="text-3xl font-bold">200+</p>
                  <p className="text-sm text-teal-200">Clients Served</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Avoid Penalties Warning */}
      <section className="py-16 px-4 bg-amber-50 border-y-4 border-amber-400">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="bg-amber-500 w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="text-white" size={32} />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-amber-900 mb-2">Avoid Penalties</h3>
              <p className="text-lg text-amber-800">
                Non-compliance with Corporate Tax regulations can result in significant penalties. Early planning ensures smooth filing and risk-free operations.
              </p>
            </div>
          </div>
        </div>
      </section>

            <RelatedServices
        services={[
          { title: 'VAT Services in UAE', description: 'End-to-end FTA-compliant VAT registration, filing, and audit support.', path: '/vat-services-uae', accent: 'blue' },
          { title: 'Accounting Services', description: 'Corporate Tax-ready financials with proper records and reconciliations.', path: '/accounting-services-uae', accent: 'indigo' },
          { title: 'Business Advisory', description: 'Strategic tax planning combined with growth and risk advisory.', path: '/business-advisory-uae', accent: 'emerald' }
        ]}
      />

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-teal-600 to-teal-700 text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Need Corporate Tax Assistance?</h2>
          <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
            Our expert team is ready to help you navigate UAE's Corporate Tax requirements with confidence.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/contact?service=corporate-tax">
              <Button size="lg" className="bg-white text-teal-700 hover:bg-gray-100 px-8 py-6 text-lg" data-testid="ct-cta-consultation-btn">
                Get Started Today
              </Button>
            </Link>
            <a href="https://wa.me/971501219341" target="_blank" rel="noopener noreferrer" data-testid="ct-whatsapp-btn">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                WhatsApp Us
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CorporateTaxUAE;
